import branding from './branding';
import seo from './seo';
import { constants } from '@match/react-component-library';
import { transparentize } from 'polished';
import { PaletteColors } from '@match/react-component-library/components/theme/types/color';

const { FONT_FACE, FONT_WEIGHT } = constants;

const opacity = {
  100: 0,
  75: 0.25,
  50: 0.5,
  25: 0.75,
  10: 0.9,
  5: 0.95,
  0: 1,
};

//                 0  1  2   3   4   5   6   7   8   9  10  11  12  13  14  15  16
const iconScale = [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64];

const iconSizes = {
  xxsmall: iconScale[2], // 8
  xsmall: iconScale[4], // 16
  small: iconScale[6], //24
  medium: iconScale[8], //32
  large: iconScale[10], //40
  xlarge: iconScale[12], //48
  xxlarge: iconScale[14], ///56
  xxxlarge: iconScale[16], // 64
};

const space = {
  none: 0,
  xsmall: 4,
  small: 8,
  smedium: 12,
  medium: 16,
  large: 20,
  xlarge: 24,
  xxlarge: 32,
  xxxlarge: 40,
};

const fontVariants = () => [
  {
    family: 'Beausite Classic',
    url: require('./static/fonts/beausite-classic-clear.woff2'),
    weight: '400',
    format: 'woff2',
  },
  {
    family: 'Beausite Classic',
    url: require('./static/fonts/beausite-classic-clear-semibold.woff2'),
    weight: '500 700',
    format: 'woff2',
  },
];

const font = {
  monospace: `'Courier New', Courier, monospace`,
  heading: `'Source Serif Pro', 'Times New Roman', Times, serif`,
  body: `'Beausite Classic', Helvetica, Arial, sans-serif`,
};

const fontSizes = {
  xxxsmall: 11,
  xxsmall: 12,
  xsmall: 14,
  small: 16,
  medium: 18,
  large: 20,
  xlarge: 28,
  xxlarge: 32,
  xxxlarge: 36,
  xxxxlarge: 40,
};

const lineHeights = {
  xxxsmall: '12px',
  xxsmall: '14px',
  xsmall: '16px',
  small: '20px',
  medium: '24px',
  large: '30px',
  xlarge: '34px',
  xxlarge: '38px',
  xxxlarge: '44px',
};

const typography = {
  heading1: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.xxxxlarge,
    letterSpacing: '-1px',
    lineHeight: lineHeights.xxxlarge,
    fontWeight: FONT_WEIGHT.BOLD,
    paddingBottom: '5px',
  },
  heading2: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.xxxlarge,
    fontWeight: FONT_WEIGHT.BOLD,
    letterSpacing: '-0.25px',
    lineHeight: lineHeights.xxlarge,
    paddingBottom: '5px',
  },
  heading3: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.xxlarge,
    letterSpacing: '-0.4px',
    lineHeight: lineHeights.xlarge,
    fontWeight: FONT_WEIGHT.BOLD,
    paddingBottom: '4px',
  },
  heading4: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.xlarge,
    letterSpacing: '-0.5px',
    lineHeight: lineHeights.large,
    paddingBottom: '2px',
    fontWeight: FONT_WEIGHT.BOLD,
  },
  heading5: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: 24,
    letterSpacing: '-0.5px',
    lineHeight: lineHeights.large,
    paddingBottom: '1px',
    fontWeight: FONT_WEIGHT.BOLD,
  },
  heading6: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.large,
    letterSpacing: '-0.1px',
    lineHeight: lineHeights.medium,
    paddingBottom: '3px',
    fontWeight: FONT_WEIGHT.BOLD,
  },
  heading7: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.small,
    letterSpacing: '0px',
    lineHeight: lineHeights.small,
    fontWeight: FONT_WEIGHT.BOLD,
  },
  heading7b: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.small,
    letterSpacing: '0px',
    lineHeight: lineHeights.small,
    fontWeight: FONT_WEIGHT.BOLD,
  },
  heading8: {
    fontFamily: FONT_FACE.HEADING,
    fontSize: fontSizes.xxsmall,
    letterSpacing: '0px',
    lineHeight: lineHeights.xsmall,
    fontWeight: FONT_WEIGHT.BOLD,
  },
  largeBody: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.large,
    lineHeight: '28px',
    fontWeight: FONT_WEIGHT.REGULAR,
  },
  standardBody: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.small,
    lineHeight: lineHeights.medium,
    fontWeight: FONT_WEIGHT.REGULAR,
  },
  smallBody: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.xsmall,
    lineHeight: lineHeights.small,
    fontWeight: FONT_WEIGHT.REGULAR,
  },
  meta: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.xxsmall,
    lineHeight: '18px',
    fontWeight: FONT_WEIGHT.REGULAR,
  },
  legal: {
    fontFamily: FONT_FACE.BODY,
    fontSize: fontSizes.xxxsmall,
    lineHeight: '17px',
    fontWeight: FONT_WEIGHT.REGULAR,
  },
};

const coreOrange = {
  150: '#C85934', // deprecated
  125: '#E1643B', // deprecated
  100: '#FA6F41',
  75: '#FA916C',
  50: '#FBB398',
  25: '#FBD6C3',
  10: '#FCEADE',
};

const coreYellow = {
  150: '#CC8400', // deprecated
  125: '#E89703', // deprecated
  100: '#FFA500',
  75: '#FEBA3C',
  50: '#FECF78',
  25: '#FDE3B3',
  10: '#FCF0D7',
};

const coreMagenta = {
  100: '#DD66BB',
  75: '#E58AC8',
  50: '#ECAFD5',
  25: '#F4D4E2',
  10: '#F9E9EA',
};

const coreBlush = {
  100: '#FFF1EC',
  75: '#FEF3ED',
  50: '#FEF4ED',
  25: '#FDF6EE',
  10: '#FCF7EF ',
};

const coreWhite = {
  100: '#FFFFFF',
  75: '#FFFFFF',
  50: '#FFFFFF',
  25: '#FFFFFF',
  10: '#FFFFFF',
};

const featureSuperblue = {
  150: '#12289A', // deprecated
  125: '#152DAD', // deprecated
  100: '#1732C0',
  75: '#5063CC',
  50: '#8995D7',
  25: '#C3C7E3',
  10: '#E5E4EA ',
};

const functionalGold = {
  150: '#FDE3B3', // deprecated
  125: '#FDE3B3', // deprecated
  100: '#FDE3B3',
  75: '#FDEAC6',
  50: '#FEF1D9',
  25: '#FEF8EC',
  10: '#FFFCF7',
};

const functionalGreen = {
  150: '#54962F', // deprecated
  125: '#54962F', // deprecated
  100: '#54962F',
  75: '#7EAF5F',
  50: '#A8C78F',
  25: '#D2DFBF',
  10: '#EBEEDC',
};

const functionalRuby = {
  150: '#CC0429', // deprecated
  125: '#CC0429', // deprecated
  100: '#CC0429',
  75: '#D9435F',
  50: '#E58294',
  25: '#F2C0CA',
  10: '#FAE6EA',
};

const grays = {
  100: '#1D1C1C',
  75: '#555351',
  50: '#8D8A85',
  25: '#C4C1BA',
  10: '#E6E2DA',
  5: '#EDEDEE',
  0: '#FCF8EF',
};

const colors = {
  transparent: 'transparent',
};

const designSystemColors = {
  core1_150: coreYellow[150],
  core1_125: coreYellow[125],
  core1_100: coreYellow[100],
  core1_75: coreYellow[75],
  core1_50: coreYellow[50],
  core1_25: coreYellow[25],
  core1_10: coreYellow[10],
  core1_75_opaque: transparentize(opacity[75], coreYellow[100]),
  core1_50_opaque: transparentize(opacity[50], coreYellow[100]),
  core1_25_opaque: transparentize(opacity[25], coreYellow[100]),
  core1_10_opaque: transparentize(opacity[10], coreYellow[100]),

  core2_150: coreMagenta[100],
  core2_125: coreMagenta[100],
  core2_100: coreMagenta[100],
  core2_75: coreMagenta[75],
  core2_50: coreMagenta[50],
  core2_25: coreMagenta[25],
  core2_10: coreMagenta[10],
  core2_75_opaque: transparentize(opacity[75], coreMagenta[100]),
  core2_50_opaque: transparentize(opacity[50], coreMagenta[100]),
  core2_25_opaque: transparentize(opacity[25], coreMagenta[100]),
  core2_10_opaque: transparentize(opacity[10], coreMagenta[100]),

  core3_150: coreOrange[150],
  core3_125: coreOrange[125],
  core3_100: coreOrange[100],
  core3_75: coreOrange[75],
  core3_50: coreOrange[50],
  core3_25: coreOrange[25],
  core3_10: coreOrange[10],
  core3_75_opaque: transparentize(opacity[75], coreOrange[100]),
  core3_50_opaque: transparentize(opacity[50], coreOrange[100]),
  core3_25_opaque: transparentize(opacity[25], coreOrange[100]),
  core3_10_opaque: transparentize(opacity[10], coreOrange[100]),

  core4_100: coreBlush[100],
  core4_75: coreBlush[75],
  core4_50: coreBlush[50],
  core4_25: coreBlush[25],
  core4_10: coreBlush[10],
  core4_75_opaque: transparentize(opacity[75], coreBlush[100]),
  core4_50_opaque: transparentize(opacity[50], coreBlush[100]),
  core4_25_opaque: transparentize(opacity[25], coreBlush[100]),
  core4_10_opaque: transparentize(opacity[10], coreBlush[100]),

  core5_100: coreWhite[100],
  core5_75: coreWhite[75],
  core5_50: coreWhite[50],
  core5_25: coreWhite[25],
  core5_10: coreWhite[10],
  core5_75_opaque: transparentize(opacity[75], coreWhite[100]),
  core5_50_opaque: transparentize(opacity[50], coreWhite[100]),
  core5_25_opaque: transparentize(opacity[25], coreWhite[100]),
  core5_10_opaque: transparentize(opacity[10], coreWhite[100]),

  feature1_150: featureSuperblue[150],
  feature1_125: featureSuperblue[125],
  feature1_100: featureSuperblue[100],
  feature1_75: featureSuperblue[75],
  feature1_50: featureSuperblue[50],
  feature1_25: featureSuperblue[25],
  feature1_10: featureSuperblue[10],
  feature1_75_opaque: transparentize(opacity[75], featureSuperblue[100]),
  feature1_50_opaque: transparentize(opacity[50], featureSuperblue[100]),
  feature1_25_opaque: transparentize(opacity[25], featureSuperblue[100]),
  feature1_10_opaque: transparentize(opacity[10], featureSuperblue[100]),

  functional1_150: functionalGreen[150],
  functional1_125: functionalGreen[125],
  functional1_100: functionalGreen[100],
  functional1_75: functionalGreen[75],
  functional1_50: functionalGreen[50],
  functional1_25: functionalGreen[25],
  functional1_10: functionalGreen[10],
  functional1_75_opaque: transparentize(opacity[75], functionalGreen[100]),
  functional1_50_opaque: transparentize(opacity[50], functionalGreen[100]),
  functional1_25_opaque: transparentize(opacity[25], functionalGreen[100]),
  functional1_10_opaque: transparentize(opacity[10], functionalGreen[100]),

  functional2_150: functionalRuby[150],
  functional2_125: functionalRuby[125],
  functional2_100: functionalRuby[100],
  functional2_75: functionalRuby[75],
  functional2_50: functionalRuby[50],
  functional2_25: functionalRuby[25],
  functional2_10: functionalRuby[10],
  functional2_75_opaque: transparentize(opacity[75], functionalRuby[100]),
  functional2_50_opaque: transparentize(opacity[50], functionalRuby[100]),
  functional2_25_opaque: transparentize(opacity[25], functionalRuby[100]),
  functional2_10_opaque: transparentize(opacity[10], functionalRuby[100]),

  functional3_150: functionalGold[150],
  functional3_125: functionalGold[125],
  functional3_100: functionalGold[100],
  functional3_75: functionalGold[75],
  functional3_50: functionalGold[50],
  functional3_25: functionalGold[25],
  functional3_10: functionalGold[10],
  functional3_75_opaque: transparentize(opacity[75], functionalGold[150]),
  functional3_50_opaque: transparentize(opacity[50], functionalGold[150]),
  functional3_25_opaque: transparentize(opacity[25], functionalGold[150]),
  functional3_10_opaque: transparentize(opacity[10], functionalGold[150]),

  gray_100: grays[100],
  gray_75: grays[75],
  gray_50: grays[50],
  gray_25: grays[25],
  gray_10: grays[10],
  gray_5: grays[5],
  gray_0: grays[0],
  gray_75_opaque: transparentize(opacity[75], grays[100]),
  gray_50_opaque: transparentize(opacity[50], grays[100]),
  gray_25_opaque: transparentize(opacity[25], grays[100]),
  gray_10_opaque: transparentize(opacity[10], grays[100]),
  gray_5_opaque: transparentize(opacity[5], grays[100]),
  gray_0_opaque: transparentize(opacity[0], grays[100]),
};

const semanticGrays = {
  core1_100: grays[100],
  primary2: grays[75],
  primary3: '#6A6A6B',
  primary4: '#949495',
  primary5: '#BFBFBF',
  primary6: grays[10],
  secondary1: '#FAFAFA',
  secondary2: '#BABBBB',
  secondary3: '#8C8D8D',
  secondary4: '#5F5F60',
};

const semanticColors = {
  ctaPrimary: coreOrange[100],
  ctaSecondary: coreMagenta[100],

  focusPrimary: coreOrange[10],

  textPrimary: semanticGrays.primary2,
  textSecondary: '#6A6A6B',
  textTertiary: grays[50],

  error: functionalRuby[100],
  success: functionalGreen[100],
  warning: grays[100],
  info: coreOrange[100],
  link: coreOrange[100],

  disabled: grays[25],
  stroke: grays[10],
  transparent: colors.transparent,

  dark: grays[100],
  darkSubcopy: semanticGrays.primary2,
  darkTertiary: grays[50],
  darkGhost: grays[25],
  light: grays[0],
  lightSubcopy: transparentize(0.3, grays[0]),
  lightTertiary: transparentize(0.5, grays[0]),
  lightGhost: transparentize(0.7, grays[0]),
};

const palette: PaletteColors = {
  ...grays,
  ...semanticColors,
  ...designSystemColors,
};

const radii = {
  corner: 0,
  roundCorner1x: 8,
  roundCorner2x: 16,
  round: 1000,
};

const components = {
  checkbox: {
    icon: 'check',
    iconSize: 6,
    width: 24,
    height: 24,
    borderRadius: `4px`,
    rowSpace: space.xsmall,
    label: {
      space: space.small,
    },
    checked: {
      border: `1px solid ${palette.core3_100}`,
      color: 'light',
      backgroundColor: palette.core3_100,
    },
    unchecked: {
      border: `1px solid ${palette.gray_25}`,
    },
    focused: {
      border: `1px solid ${palette.core3_25}`,
      boxShadow: `0 0 0px 4px ${palette.core3_25}`,
    },
    hover: {
      border: `1px solid ${palette.core3_100}`,
      boxShadow: `0`,
      backgroundColor: palette.core3_25,
    },
    disabled: {
      opacity: 0.3,
    },
  },
  logo: {
    url: () => require('./static/theme-logo.svg'),
    light: '#FFFFFF',
    dark: '#141651',
  },
  icon: {
    prefix: 'stir-',
    size: iconSizes,
    url: () => require('./static/theme-icons.svg'), // Requires webpack file loader
  },
};

export default {
  branding,
  font,
  fontVariants,
  fontProviders: {
    google: {
      href: 'family=Source+Serif+Pro:ital,wght@0,400;0,600;1,400;1,600&display=fallback',
    },
  },
  lineHeights,
  palette,
  radii,
  seo,
  typography,
  components: {
    ...components,
    logo: {
      url: () => require('./static/theme-logo.svg'),
      light: '#FFFFFF',
      dark: coreOrange[100],
    },
    icon: {
      prefix: 'stir-',
      size: iconSizes,
      url: () => require('./static/theme-icons.svg'), // Requires webpack file loader
    },
  },
};
